import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import CompanyTabs from '../../components/institute/Jobs/InstituteAddCompany/CompanyTabs';
import CompanyFormTabs from '../../components/institute/Jobs/InstituteAddCompany/CompanyFormTabs';
import CompanyFormActions from '../../components/institute/Jobs/InstituteAddCompany/CompanyFormActions';
import PageHeader from '../../components/PageHeader';

const InstituteAddCompany = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(companyId);

  // Initial form state
  const initialFormData = {
  companyName: '',
  companyLogo: null,
  address1: '',
  address2: '',
  state: '',
  city: '',
  companyWebsite: '',
  organisationSize: '',
  status: isEditMode ? null : 1, 
  aboutUs: '',
  contactPersonName: '',
  contactPersonNumber: '',
  contactPersonEmail: '',
  industry_id: ''
};

  const initialTabValidation = {
    0: false,
    1: false,
    2: false
  };

  const [activeTab, setActiveTab] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [logoPreview, setLogoPreview] = useState(null);
  const [tabValidation, setTabValidation] = useState(initialTabValidation);

  // Loading and submission states
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Store original company data for reference
  const [companyData, setCompanyData] = useState(null);

  const InstituteId = sessionStorage.getItem("instituteId");
  // State and City API related states
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;
  const [allStates, setAllStates] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [stateSearchTerm, setStateSearchTerm] = useState("");
  const [allCities, setAllCities] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [citySearchTerm, setCitySearchTerm] = useState("");
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [stateDropdownTimeout, setStateDropdownTimeout] = useState(null);
  const [cityDropdownTimeout, setCityDropdownTimeout] = useState(null);
  const [localError, setLocalError] = useState("");
  const [industries, setIndustries] = useState([]);

  // Track initialization status
  const [statesLoaded, setStatesLoaded] = useState(false);
  const [citiesLoaded, setCitiesLoaded] = useState(false);
  const [dataInitialized, setDataInitialized] = useState(false);
  const [citiesLoading, setCitiesLoading] = useState(false);

  const tabs = [
    { id: 0, title: 'Company Basic Information', icon: '1' },
    { id: 1, title: 'Company Address', icon: '2' },
    { id: 2, title: 'Contact Person Information', icon: '3' }
  ];

  const getStateNameById = (stateId, statesArray) => {
    
    if (!stateId) {
      return '';
    }
    
    if (!statesArray?.length) {
      return '';
    }

    try {
      const normalizedStateId = String(stateId);

      const state = statesArray.find(s => String(s.id) === normalizedStateId);

      if (!state) {
        return '';
      }

      return state.name || '';

    } catch (error) {
      console.error('Error in getStateNameById:', error);
      return '';
    }
  };

  // Helper function to find city name by ID with error handling
  const getCityNameById = (cityId, citiesArray) => {
    if (!cityId || !citiesArray?.length) return '';
    try {
      const city = citiesArray.find(c => String(c.id) === String(cityId));
      return city?.name || '';
    } catch (error) {
      console.error('Error finding city name:', error);
      return '';
    }
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData(initialFormData);
    setErrors({});
    setLogoPreview(null);
    setTabValidation(initialTabValidation);
    setActiveTab(0);
    setStateSearchTerm('');
    setCitySearchTerm('');
    setSelectedState('');
    setSelectedCity('');
    setShowStateDropdown(false);
    setShowCityDropdown(false);
    setSubmitSuccess(false);
    setSubmitError('');
    setLocalError('');
    setStatesLoaded(false);
    setCitiesLoaded(false);
    setDataInitialized(false);
    setCompanyData(null);
    setCitiesLoading(false);
  };

  // Show success alert
  const showSuccessAlert = (message) => {
    Swal.fire({
      title: 'Success!',
      text: message,
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#28a745',
      allowOutsideClick: false,
      allowEscapeKey: false
    }).then((result) => {
      if (result.isConfirmed) {
        navigate('/company-list');
      }
    });
  };

  // Show error alert
  const showErrorAlert = (message) => {
    Swal.fire({
      title: 'Error!',
      text: message,
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: '#dc3545',
      allowOutsideClick: false,
      allowEscapeKey: false
    });
  };

  // Fetch states first
  const fetchStates = async () => {
    if (!token) {
      return [];
    }

    try {
      const cachedStates = localStorage.getItem("states");
      const cachedAt = localStorage.getItem("states_cached_at");
      const now = Date.now();

      if (cachedStates && cachedAt && now - cachedAt < 86400000) {
        const states = JSON.parse(cachedStates);
        setAllStates(states);
        setFilteredStates(states);
        setStatesLoaded(true);
        return states;
      }

      const response = await fetch(`${LMS}/api/get-state`, { 
        headers: { Authorization: token } 
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data?.data)) {
        setAllStates(data.data);
        setFilteredStates(data.data);
        localStorage.setItem("states", JSON.stringify(data.data));
        localStorage.setItem("states_cached_at", now.toString());
        setStatesLoaded(true);
        return data.data;
      } else {
        throw new Error('Invalid data format received');
      }
    } catch (error) {
      setLocalError("Failed to load states: " + error.message);
      showErrorAlert("Failed to load states: " + error.message);
      return [];
    }
  };

  // Fetch industries
  const fetchIndustries = async () => {
    if (!token) return [];
    try {
      const response = await fetch(`${LMS}/api/get-industries`, {
        headers: { Authorization: token }
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (Array.isArray(data?.data)) {
        setIndustries(data.data);
        return data.data;
      } else {
        throw new Error('Invalid data format received');
      }
    } catch (error) {
      console.error('Error fetching industries:', error);
      return [];
    }
  };

  // Fetch cities for a specific state
  const fetchCities = async (stateId) => {
    if (!stateId || !token) {
      setAllCities([]);
      setFilteredCities([]);
      setCitiesLoaded(false);
      return [];
    }

    setCitiesLoading(true);

    try {
      const response = await fetch(`${LMS}/api/get-city?state_id=${stateId}`, { 
        headers: { Authorization: token } 
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data && Array.isArray(data.data)) {
        setAllCities(data.data);
        setFilteredCities(data.data);
        setCitiesLoaded(true);
        return data.data;
      } else {
        setAllCities([]);
        setFilteredCities([]);
        setCitiesLoaded(false);
        return [];
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
      setLocalError("Failed to load cities: " + error.message);
      showErrorAlert("Failed to load cities: " + error.message);
      setAllCities([]);
      setFilteredCities([]);
      setCitiesLoaded(false);
      return [];
    } finally {
      setCitiesLoading(false);
    }
  };

  // Fetch company data for editing
  const fetchCompanyData = async (statesArray = allStates) => {
    if (!isEditMode || !companyId) return;
    
    setIsLoading(true);
    setLocalError('');
    
    try {
      const response = await fetch(`${LMS}/api/organisation/${companyId}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const company = result.data;

      setCompanyData(company);

      const newFormData = {
        companyName: company.organisation_name || '',
        companyLogo: null,
        address1: company.address1 || '',
        address2: company.address2 || '',
        state: company.state?.toString() || '',
        city: company.city?.toString() || '',
        companyWebsite: company.industry_domain || '',
        organisationSize: company.organisation_size || '',
        status: company.status !== undefined ? parseInt(company.status) : 1, // Properly parse the status from API
        aboutUs: company.about_us || '',
        contactPersonName: company.contact_person_name || '',
        contactPersonNumber: company.contact_person_number || '',
        contactPersonEmail: company.contact_person_email || '',
        industry_id: company.industry_id?.toString() || ''
      };

      setFormData(newFormData);
      setSelectedState(company.state?.toString() || '');
      setSelectedCity(company.city?.toString() || '');

      // Convert state ID to name and set search term
      if (company.state && statesArray.length > 0) {
        const stateName = getStateNameById(company.state, statesArray);
        setStateSearchTerm(stateName);
      }

      // Fetch cities for the selected state and set city name
      if (company.state) {
        const cities = await fetchCities(company.state);
        if (company.city && cities.length > 0) {
          const cityName = getCityNameById(company.city, cities);
          setCitySearchTerm(cityName);
        }
      }

      if (company.organisation_logo) {
        setLogoPreview(company.organisation_logo);
      }

      setDataInitialized(true);

    } catch (error) {
      console.error('Error fetching company data:', error);
      setLocalError('Failed to load company data. Please try again.');
      showErrorAlert('Failed to load company data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Initialize data in proper order
  useEffect(() => {
    const initializeData = async () => {
      // First, always fetch states and industries
      const [states] = await Promise.all([
        fetchStates(),
        fetchIndustries()
      ]);
      
      // Then fetch company data if in edit mode
      if (isEditMode && companyId && states.length > 0) {
        await fetchCompanyData(states);
      }
    };

    initializeData();
  }, [companyId, isEditMode, token, LMS]);

  // Handle state name update when states are loaded and company data exists
  useEffect(() => {
    if (statesLoaded && companyData && companyData.state && !stateSearchTerm) {
      const stateName = getStateNameById(companyData.state, allStates);
      if (stateName) {
        setStateSearchTerm(stateName);
      }
    }
  }, [statesLoaded, companyData, allStates, stateSearchTerm]);

  // Handle city name update when cities are loaded
  useEffect(() => {
    if (citiesLoaded && companyData && companyData.city && !citySearchTerm) {
      const cityName = getCityNameById(companyData.city, allCities);
      if (cityName) {
        setCitySearchTerm(cityName);
      }
    }
  }, [citiesLoaded, companyData, allCities, citySearchTerm]);

  // Validate all tabs
  const validateAllTabs = (currentFormData = formData) => {
    const newTabValidation = { ...initialTabValidation };
    
    // Tab 0: Basic Information - Include companyWebsite as required
    if (currentFormData.companyName.trim() && 
        currentFormData.companyWebsite.trim() && 
        currentFormData.organisationSize && 
        currentFormData.industry_id &&
        currentFormData.aboutUs.trim()) {
      newTabValidation[0] = true;
    }
    
    // Tab 1: Address
    if (currentFormData.address1.trim() && currentFormData.state && currentFormData.city) {
      newTabValidation[1] = true;
    }
    
    // Tab 2: Contact Person
    if (currentFormData.contactPersonName.trim() && 
        currentFormData.contactPersonNumber.trim() && 
        currentFormData.contactPersonEmail.trim() &&
        /^\d{10}$/.test(currentFormData.contactPersonNumber) &&
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(currentFormData.contactPersonEmail)) {
      newTabValidation[2] = true;
    }
    
    setTabValidation(newTabValidation);
    return newTabValidation;
  };

  // Validate tabs when form data changes
  useEffect(() => {
    if (formData.companyName || formData.state || formData.city) {
      validateAllTabs();
    }
  }, [formData]);

  // Handle state change - fetch cities when state changes
  useEffect(() => {
    if (selectedState && selectedState !== formData.state) {
      setSelectedCity("");
      setCitySearchTerm("");
      setFormData(prev => ({ ...prev, city: "" }));
      
      fetchCities(selectedState);
    }
  }, [selectedState]);

  // State input handlers
  const handleStateInputChange = (e) => {
    const value = e.target.value;
    setStateSearchTerm(value);
    
    if (value.trim() === "") {
      setFilteredStates(allStates);
      setSelectedState("");
      setFormData(prev => ({ ...prev, state: "" }));
      setSelectedCity("");
      setCitySearchTerm("");
      setFormData(prev => ({ ...prev, city: "" }));
      setAllCities([]);
      setFilteredCities([]);
      setCitiesLoaded(false);
    } else {
      const filtered = allStates.filter(state => 
        state.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredStates(filtered);
    }
  };

  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setCitySearchTerm(value);
    
    if (value.trim() === "") {
      setFilteredCities(allCities);
      setSelectedCity("");
      setFormData(prev => ({ ...prev, city: "" }));
    } else {
      const filtered = allCities.filter(city => 
        city.name.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredCities(filtered);
    }
  };

  const handleStateSelect = (state) => {
    setSelectedState(state.id.toString());
    setFormData(prev => ({ ...prev, state: state.id.toString() }));
    setStateSearchTerm(state.name);
    setShowStateDropdown(false);
    clearTimeout(stateDropdownTimeout);
    
    // Reset city when state changes
    setSelectedCity("");
    setCitySearchTerm("");
    setFormData(prev => ({ ...prev, city: "" }));
    setAllCities([]);
    setFilteredCities([]);
    setCitiesLoaded(false);
    
    if (errors.state) {
      setErrors(prev => ({ ...prev, state: '' }));
    }
    
    // Fetch cities for the new state
    fetchCities(state.id.toString());
  };

  const handleCitySelect = (city) => {
    setSelectedCity(city.id.toString());
    setFormData(prev => ({ ...prev, city: city.id.toString() }));
    setCitySearchTerm(city.name);
    setShowCityDropdown(false);
    clearTimeout(cityDropdownTimeout);
    
    if (errors.city) {
      setErrors(prev => ({ ...prev, city: '' }));
    }
  };

  const onStateInputBlur = () => {
    setStateDropdownTimeout(setTimeout(() => setShowStateDropdown(false), 200));
  };

  const onCityInputBlur = () => {
    setCityDropdownTimeout(setTimeout(() => setShowCityDropdown(false), 200));
  };

  const handleStateInputFocus = () => {
    clearTimeout(stateDropdownTimeout);
    setShowStateDropdown(true);
  };

  const handleCityInputFocus = () => {
    clearTimeout(cityDropdownTimeout);
    if (selectedState && allCities.length > 0) {
      setShowCityDropdown(true);
    }
  };

  // Convert image file to base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Form validation
  const validateForm = () => {
  const newErrors = {};

  // Basic Information validation
  if (!formData.companyName.trim()) {
    newErrors.companyName = 'Company name is required';
  }
  if (!formData.companyWebsite.trim()) {
    newErrors.companyWebsite = 'Company website is required';
  } else if (!/^https?:\/\/.+/.test(formData.companyWebsite)) {
    newErrors.companyWebsite = 'Please enter a valid website URL (include http:// or https://)';
  }
  if (!formData.organisationSize) {
    newErrors.organisationSize = 'Organization size is required';
  }
  if (!formData.industry_id) {
    newErrors.industry_id = 'Industry is required';
  }
  if (!formData.aboutUs.trim()) {
    newErrors.aboutUs = 'About us is required';
  }

  // Address validation
  if (!formData.address1.trim()) {
    newErrors.address1 = 'Address line 1 is required';
  }
  if (!formData.state) {
    newErrors.state = 'State is required';
  }
  if (!formData.city) {
    newErrors.city = 'City is required';
  }

  // Contact person validation
  if (!formData.contactPersonName.trim()) {
    newErrors.contactPersonName = 'Contact person name is required';
  }
  if (!formData.contactPersonNumber.trim()) {
    newErrors.contactPersonNumber = 'Contact person number is required';
  } else if (!/^\d{10}$/.test(formData.contactPersonNumber)) {
    newErrors.contactPersonNumber = 'Contact number must be 10 digits';
  }
  if (!formData.contactPersonEmail.trim()) {
    newErrors.contactPersonEmail = 'Contact person email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.contactPersonEmail)) {
    newErrors.contactPersonEmail = 'Please enter a valid email address';
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};

  // Submit form to API
  const handleSubmit = async () => {
    if (!validateForm()) {
      setSubmitError('Please fix the validation errors before submitting');
      showErrorAlert('Please fix the validation errors before submitting');
      return;
    }

    setIsSubmitting(true);
    setSubmitError('');
    setSubmitSuccess(false);

    try {
      let logoBase64 = null;
      
      if (formData.companyLogo instanceof File) {
        logoBase64 = await convertToBase64(formData.companyLogo);
      } else if (isEditMode && logoPreview && !formData.companyLogo) {
        logoBase64 = logoPreview;
      }

      const payload = {
        institute_id: parseInt(InstituteId),
        organisation_name: formData.companyName,
        state: formData.state,
        city: formData.city,
        address1: formData.address1,
        address2: formData.address2 || '',
        contact_person_name: formData.contactPersonName,
        contact_person_number: formData.contactPersonNumber,
        contact_person_email: formData.contactPersonEmail,
        organisation_size: formData.organisationSize,
        industry_domain: formData.companyWebsite || '',
        industry_id: formData.industry_id,
        status: formData.status.toString(),
        organisation_logo: logoBase64 || '',
        about_us: formData.aboutUs
      };

      const apiUrl = isEditMode ? `${LMS}/api/organisation/${companyId}` : `${LMS}/api/organisation`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(apiUrl, {
        method: method,
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (response.ok) {
        setSubmitSuccess(true);
        setSubmitError('');
        
        const successMessage = isEditMode 
          ? 'Company information has been updated successfully!' 
          : 'Company has been created successfully!';
        showSuccessAlert(successMessage);
        
      } else {
        const errorMessage = result.message || `Failed to ${isEditMode ? 'update' : 'create'} organization. Please try again.`;
        setSubmitError(errorMessage);
        showErrorAlert(errorMessage);
      }
    } catch (error) {
      const errorMessage = 'Network error. Please check your connection and try again.';
      setSubmitError(errorMessage);
      showErrorAlert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading spinner while fetching data in edit mode
  if (isEditMode && isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <PageHeader title={isEditMode ? 'Edit Company' : 'Add Company'}
        breadcrumbs={[
          { label: "Dashboard", path: "/institute-dashboard" },
          { label: "Company Listings", path: "/company-list" },
          { label: isEditMode ? 'Edit Company' : 'Add Company', active: true }
        ]}
      />

      <div className="card-body mt-4">
        {/* Step Tabs */}
        <CompanyTabs
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          tabValidation={tabValidation}
          errors={errors}
        />

        {/* Form Sections */}
        <CompanyFormTabs
          activeTab={activeTab}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          logoPreview={logoPreview}
          setLogoPreview={setLogoPreview}
          tabValidation={tabValidation}
          setTabValidation={setTabValidation}
          isEditMode={isEditMode}
          // State and City props
          allStates={allStates}
          filteredStates={filteredStates}
          selectedState={selectedState}
          stateSearchTerm={stateSearchTerm}
          allCities={allCities}
          filteredCities={filteredCities}
          selectedCity={selectedCity}
          citySearchTerm={citySearchTerm}
          showStateDropdown={showStateDropdown}
          showCityDropdown={showCityDropdown}
          handleStateInputChange={handleStateInputChange}
          handleCityInputChange={handleCityInputChange}
          handleStateSelect={handleStateSelect}
          handleCitySelect={handleCitySelect}
          onStateInputBlur={onStateInputBlur}
          onCityInputBlur={onCityInputBlur}
          setShowStateDropdown={setShowStateDropdown}
          setShowCityDropdown={setShowCityDropdown}
          handleStateInputFocus={handleStateInputFocus}
          handleCityInputFocus={handleCityInputFocus}
          citiesLoading={citiesLoading}
          citiesLoaded={citiesLoaded}
          industries={industries}
        />

        {/* Navigation Buttons */}
        <CompanyFormActions
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          formData={formData}
          setFormData={setFormData}
          errors={errors}
          setErrors={setErrors}
          tabValidation={tabValidation}
          setTabValidation={setTabValidation}
          logoPreview={logoPreview}
          setLogoPreview={setLogoPreview}
          tabs={tabs}
          isEditMode={isEditMode}
          handleSubmit={handleSubmit}
          isSubmitting={isSubmitting}
          validateForm={validateForm}
        />
      </div>
    </>
  );
};

export default InstituteAddCompany;